import { cn, focusRing } from "../ui";
import { dayOfMonth, fullDate, groupByMonth, monthName, weekdayShort } from "./calendarDays";

/** The DOM id of a month heading, for a month jump: `monthAnchorId("cal", "2026-03")`. */
export const monthAnchorId = (idPrefix, monthKey) => `${idPrefix}-${monthKey}`;

/**
 * CalendarAgenda — days in date order under month headings. One row per entry; the row opens the entry's detail and
 * an optional trailing slot holds the row's own actions.
 *
 * It shows what it is given and decides nothing: an entry is `{ key, date, title, kind, meta?, flag?, marked?, past?,
 * actions? }`, where `kind` and `meta` are finished words from the page. `marked` draws the quiet rule that sets a
 * business entry apart from an official one; meaning is always carried by the words, never by the rule alone.
 * On phones each row recomposes to two lines (date and title, then kind and meta).
 */
export default function CalendarAgenda({ entries, onOpen, today, idPrefix = "calendar", label = "Calendar", className }) {
  const groups = groupByMonth(entries);
  return (
    <div data-ui="calendar-agenda" aria-label={label} className={className}>
      {groups.map(({ month, entries: rows }) => (
        <section key={month} aria-labelledby={monthAnchorId(idPrefix, month)}>
          <h3
            id={monthAnchorId(idPrefix, month)}
            tabIndex={-1}
            className="sticky top-0 z-[1] scroll-mt-16 border-y border-subtle bg-surface-sunken px-4 py-1.5 text-cell-head text-secondary focus:outline-none first:border-t-0"
          >
            {monthName(month)}
          </h3>
          <ul className="divide-y divide-subtle">
            {rows.map((entry, index) => {
              const repeatsDate = index > 0 && rows[index - 1].date === entry.date;
              const isToday = today && entry.date === today;
              return (
                <li key={entry.key} className={cn("relative flex items-stretch", entry.marked && "border-l-2 border-strong")}>
                  <button
                    type="button"
                    onClick={() => onOpen?.(entry)}
                    aria-label={`${fullDate(entry.date)}, ${entry.title}, ${entry.kind}`}
                    className={cn(
                      "grid min-h-row w-full min-w-0 flex-1 grid-cols-[4.25rem_minmax(0,1fr)] items-baseline gap-x-3 px-4 py-2.5 text-left transition-colors hover:bg-hover max-md:min-h-touch",
                      "md:grid-cols-[5rem_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1.4fr)] md:py-2",
                      entry.marked && "pl-[14px]",
                      focusRing,
                      "focus-visible:outline-offset-[-2px]"
                    )}
                  >
                    <span
                      aria-current={isToday ? "date" : undefined}
                      className={cn(
                        "row-span-2 whitespace-nowrap text-cell tabular-nums md:row-span-1",
                        repeatsDate && "invisible",
                        isToday ? "font-medium text-accent underline underline-offset-4" : entry.past ? "text-tertiary" : "text-secondary"
                      )}
                    >
                      {weekdayShort(entry.date)} {dayOfMonth(entry.date)}
                    </span>
                    <span className={cn("min-w-0 text-cell font-medium", entry.past ? "text-secondary" : "text-foreground")}>
                      <span className="break-words">{entry.title}</span>
                      {entry.flag ? <span className="ml-2 font-normal text-secondary">{entry.flag}</span> : null}
                    </span>
                    <span className="col-start-2 min-w-0 text-cell text-secondary md:col-start-auto">
                      {entry.kind}
                      {entry.meta ? <span className="md:hidden"> · {entry.meta}</span> : null}
                    </span>
                    <span className="hidden min-w-0 text-cell text-secondary md:block">{entry.meta}</span>
                  </button>
                  {entry.actions ? <div className="flex shrink-0 items-center pr-2">{entry.actions}</div> : <div aria-hidden="true" className="w-11 shrink-0 max-md:hidden" />}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
